import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { invalidateKeys, queryKeys } from '@/lib/query-keys';

/** What has to change for a saved package to hold the edited services. */
export interface PackageItemChanges {
  add: { serviceId: string; quantity: number; sortOrder: number }[];
  update: { itemId: string; quantity: number; sortOrder: number }[];
  remove: string[];
}

/**
 * Bring a saved package's services in line with the editor.
 *
 * Each item has its own endpoint, so this is several calls, not one. Additions
 * go FIRST and removals LAST: the server does not stop a package losing its
 * last service, so the order is what keeps one from passing through empty when
 * every service is swapped for another.
 */
export const useSyncPackageItems = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      packageId,
      changes,
    }: {
      packageId: string;
      changes: PackageItemChanges;
    }) => {
      for (const item of changes.add) {
        await apiClient.post(`packages/${packageId}/items`, item);
      }
      for (const { itemId, ...item } of changes.update) {
        await apiClient.put(`packages/${packageId}/items/${itemId}`, item);
      }
      for (const itemId of changes.remove) {
        await apiClient.delete(`packages/${packageId}/items/${itemId}`);
      }
    },
    onSettled: () => {
      // Settled, not success: a half-applied sync must still refresh the list.
      invalidateKeys(queryClient, queryKeys.packages.all());
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update the package services');
    },
  });

  return {
    syncPackageItemsAsync: mutation.mutateAsync,
    isSyncing: mutation.isPending,
  };
};
